import React, { useMemo } from "react";

/* ============================================================
   GOLDEN OBSERVATORY  ·  motif components
   Nature + Space + Warm Tech, entirely inside the warm palette
   All decorative — pass aria-hidden.
   ============================================================ */

// Concentric brass orbit rings; optional ticks like an astrolabe.
export function OrbitRing({ size = 320, className = "", strokeOpacity = 0.35, ticks = false }) {
  const half = size / 2;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${size} ${size}`}
      style={{ width: size, height: size }}
      className={className}
    >
      <g fill="none" stroke="#B08830" strokeWidth="1">
        <circle cx={half} cy={half} r={Math.max(0, half - 4)}   opacity={strokeOpacity} />
        <circle cx={half} cy={half} r={Math.max(0, half - 28)}  opacity={strokeOpacity * 0.75} />
        <circle cx={half} cy={half} r={Math.max(0, half - 72)}  opacity={strokeOpacity * 0.55} />
        <circle cx={half} cy={half} r={Math.max(0, half - 140)} opacity={strokeOpacity * 0.35} strokeDasharray="2 6" />
      </g>
      {ticks && (
        <g stroke="#B08830" strokeWidth="1" opacity={strokeOpacity}>
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i * Math.PI) / 12;
            const outer = half - 4;
            const inner = half - 10;
            return (
              <line
                key={`tick-${i}`}
                x1={half + Math.cos(a) * inner}
                y1={half + Math.sin(a) * inner}
                x2={half + Math.cos(a) * outer}
                y2={half + Math.sin(a) * outer}
              />
            );
          })}
        </g>
      )}
      {/* single "planet" node on the outer ring */}
      <circle cx={half + (half - 4)} cy={half} r="3" fill="#B8622A" />
    </svg>
  );
}

// A wireframe leaf schematic in ochre — "engineering botanical".
export function LeafSchematic({ className = "", stroke = "#B08830", opacity = 0.35 }) {
  return (
    <svg viewBox="0 0 220 320" aria-hidden="true" className={className} fill="none" stroke={stroke} strokeWidth="1" opacity={opacity}>
      <path d="M110 20 C 40 90, 40 230, 110 300 C 180 230, 180 90, 110 20 Z" />
      <line x1="110" y1="20" x2="110" y2="300" />
      {Array.from({ length: 9 }).map((_, i) => {
        const y = 40 + i * 28;
        const spread = 12 + i * 6 + (i > 4 ? (8 - i) * 4 : 0);
        return (
          <g key={`vein-${y}`}>
            <line x1="110" y1={y} x2={110 - spread * 3.6} y2={y + 14} />
            <line x1="110" y1={y} x2={110 + spread * 3.6} y2={y + 14} />
          </g>
        );
      })}
      {/* coordinate ticks */}
      {[80, 140, 200, 260].map((y) => (
        <g key={y}>
          <circle cx="110" cy={y} r="1.6" fill={stroke} />
        </g>
      ))}
    </svg>
  );
}

// Drifting warm dust motes; place inside a relative container.
export function DustMotes({ count = 14, className = "" }) {
  // useMemo so randomized positions & keys are stable across re-renders
  // (otherwise every re-render would unmount/remount every mote and reset
  // its CSS animation).
  const motes = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const size = 3 + Math.random() * 5;
        const left = Math.random() * 100;
        const top = 40 + Math.random() * 60;
        const delay = (Math.random() * 9).toFixed(2);
        const dur = (7 + Math.random() * 6).toFixed(2);
        const variant = i % 2 === 0 ? "" : " dust-mote--b";
        return (
          <span
            key={`mote-${i}-${size.toFixed(2)}-${left.toFixed(2)}`}
            className={`dust-mote${variant}`}
            style={{
              width: size,
              height: size,
              left: `${left}%`,
              top: `${top}%`,
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
            }}
          />
        );
      }),
    [count]
  );
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      {motes}
    </div>
  );
}

// A small coordinate readout ("34°42' N · 118°24' W") in DM Mono.
export function CoordReadout({ children, className = "", dark = false }) {
  return (
    <span className={`instrument-label ${dark ? "instrument-label--dark" : ""} ${className}`}>
      {children}
    </span>
  );
}

// Section index label (SECTION · 01) with a tick mark.
export function SectionIndex({ n, label, dark = false, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className={`section-index ${dark ? "text-honey" : ""}`}>{String(n).padStart(2, "0")}</span>
      <span className="w-6 h-px" style={{ background: dark ? "rgba(224,184,76,0.5)" : "rgba(184,98,42,0.5)" }} />
    </div>
  );
}

// A soft warm sun-sweep overlay (positioned absolutely inside parent).
export function SunSweep({ className = "" }) {
  return <div aria-hidden="true" className={`sun-sweep ${className}`} />;
}

// Astrolabe glyph — 8-point star inside a ring, warm brass.
export function AstrolabeGlyph({ size = 16, color = "#E0B84C" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="none" stroke={color} strokeWidth="1" opacity="0.7" />
      <g stroke={color} strokeWidth="1" opacity="0.85">
        <line x1="12" y1="2" x2="12" y2="22" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <line x1="5" y1="5" x2="19" y2="19" />
        <line x1="19" y1="5" x2="5" y2="19" />
      </g>
      <circle cx="12" cy="12" r="1.6" fill={color} />
    </svg>
  );
}

// Small brass planet marker (used inline).
export function PlanetDot({ className = "" }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block w-1.5 h-1.5 rounded-full ${className}`}
      style={{ background: "#B08830", boxShadow: "0 0 0 3px rgba(176,136,48,0.15)" }}
    />
  );
}
